import { errCode } from './apiError';
import { Role, userHasRole } from './roles';
import { identityHeading, DELETED_ACCOUNT_LABEL } from '../components/UserIdentity';

// Exact `error.code` wire values - their case is fixed by the API.
export const COACH_ERROR_CODE = {
  ALREADY_DECIDED: 'coach_reassignment_already_decided',
  ALREADY_OPEN: 'coach_reassignment_already_open',
  NO_ALTERNATE: 'no_alternate_coach',
  SAME_COACH: 'same_coach',
  NOT_FOUND: 'sleep_coach_not_found',
};

// Decided by someone else: re-read rather than resubmit. The standing decision is
// in error.details.status.
export const isAlreadyDecided = (error) => errCode(error) === COACH_ERROR_CODE.ALREADY_DECIDED;

// The reason a decision bounced, phrased for an admin who has to act on it. Every one
// of these leaves the request pending, so the same row can be decided again.
export const coachDecisionHint = (error) => {
  switch (errCode(error)) {
    case COACH_ERROR_CODE.SAME_COACH:
      return 'That is already this customer’s coach. Pick someone else.';
    case COACH_ERROR_CODE.NOT_FOUND:
      return 'That coach is no longer an active team member with the sleep coach role.';
    case COACH_ERROR_CODE.NO_ALTERNATE:
      return 'No other coach is available to take this customer. Choose one manually.';
    case COACH_ERROR_CODE.ALREADY_DECIDED:
      return 'This request was already decided by someone else.';
    default:
      return null;
  }
};

// The customer's "what went wrong?" tags. Send the wire value, render the label.
export const REASSIGNMENT_TAGS = [
  { value: 'calm', label: 'Calm' },
  { value: 'clear', label: 'Clear' },
  { value: 'relaxed', label: 'Relaxed' },
  { value: 'soothing', label: 'Soothing' },
  { value: 'focused', label: 'Focused' },
  { value: 'floating', label: 'Floating' },
  { value: 'light', label: 'Light' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'uncomfortable', label: 'Uncomfortable' },
  { value: 'tense', label: 'Tense' },
  { value: 'didnt_feel_anything', label: 'Didn’t feel anything' },
];

const TAG_LABELS = REASSIGNMENT_TAGS.reduce((all, tag) => ({ ...all, [tag.value]: tag.label }), {});

// An unknown tag renders as its wire value rather than vanishing: a new server-side
// tag should still be visible to whoever is deciding.
export const tagLabel = (value) => TAG_LABELS[value] || value;

// The positive half of the list is genuinely usable feedback, not a complaint; colour
// them apart so a queue can be read without parsing each word.
const NEGATIVE_TAGS = ['uncomfortable', 'tense', 'didnt_feel_anything'];

export const tagVariant = (value) => (NEGATIVE_TAGS.includes(value) ? 'negative' : 'info');

// Picker options from the team list (see CLAUDE.md on TeamMember ids). Presence is
// merged in by User id for display only, and is absent for the never-logged-in.
export const toCoachOptions = (members, presence = {}) =>
  (members || [])
    .filter((member) => member?.id && member.active !== false)
    .filter((member) => userHasRole(member, Role.SLEEP_COACH))
    .map((member) => ({
      id: member.id,
      userId: member.user_id || null,
      name:
        identityHeading({ name: member.name, email: member.email, phone: member.phone_number }) ??
        DELETED_ACCOUNT_LABEL,
      email: member.email || null,
      status: presence[member.user_id] || null,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

// Presence lives on the agents roster, keyed by User id.
export const presenceByUserId = (roster) =>
  (roster || []).reduce(
    (all, agent) => (agent?.user_id ? { ...all, [agent.user_id]: agent.status } : all),
    {}
  );
